import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import DialogNewsfeed from '@/components/DialogNewsfeed/DialogNewsfeed';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import Button from '@/components/UI/Button/Button';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import { IcoButtonSimple } from '@/components/UI/IcoButton/IcoButton';
import { ALIGN } from '@/components/UI/TableListEditable/TableListEditableConstants';
import TableListEditableNew from '@/components/UI/TableListEditable/TableListEditableNew';
import { SHORT_FULL_DATE_FORMAT } from '@/helpers/constants';
import { WysiwygFormatter } from '@/helpers/tables-utils';
import { getFullFormattedDate } from '@/helpers/utils';
import useAppLanguage from '@/hooks/useAppLanguage';
import useToastContext from '@/hooks/useToastContext';
import { getSettingsInfo } from '@/redux/User/user.reducer';
import administrationServices from '@/services/administrationServices';
import titleService from '@/services/titleService';
import { booleanCellRenderer, LangTextMandatory } from '@/types';
import styles from './Newsfeed.module.scss';

const COLUMNS_NAMES = {
  PUBLICATION_DATE: 'publicationDate',
  EXPIRATION_DATE: 'expirationDate',
  CATEGORY: 'category',
  CREATOR: 'creator',
  CREATION_DATE: 'creationDate',
  TITLE: 'title',
  ACTIVE: 'active',
  TEXT: 'text',
};
export interface NewsfeedINT {
  active: boolean;
  category: string;
  creationDate: string;
  creator: string;
  expirationDate: string;
  id: string;
  publicationDate: string;
  text: LangTextMandatory;
  title: LangTextMandatory;
}

interface NewsfeedProps {
  timeZone: string;
}

const Newsfeed: React.FC<NewsfeedProps> = (props) => {
  const { timeZone } = props;

  const { t } = useTranslation();
  const lang = useAppLanguage();
  const { addToast } = useToastContext();
  const [popupOpen, setPopupOpen] = useState('');
  const [selection, setSelection] = useState<string[]>([]);
  const [newsFeed, setNewsfeed] = useState<NewsfeedINT[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    titleService.updatePageTitle(t('dashboard.administration'), t('menu.newsfeed'));
  }, [t, lang]);

  const getNewsFeed = () => {
    setLoading(true);
    administrationServices.getNewsfeed()
      .then((data) => {
        setNewsfeed(data?.news);
        setSelection([]);
      })
      .catch((error) => addToast(error.message, 'ERROR'))
      .finally(() => { setLoading(false); setPopupOpen(''); });
  };

  const columns = useMemo(() => [
    { name: COLUMNS_NAMES.PUBLICATION_DATE, title: t('editableFields.publication'), flex: 1.5 },
    { name: COLUMNS_NAMES.EXPIRATION_DATE, title: t('editableFields.expiration'), flex: 1.5 },
    { name: COLUMNS_NAMES.CATEGORY, title: t('editableFields.category'), flex: 0.75 },
    { name: COLUMNS_NAMES.CREATOR, title: t('editableFields.creator'), flex: 2, align: ALIGN.CENTER },
    { name: COLUMNS_NAMES.CREATION_DATE, title: t('editableFields.timestampCreating'), flex: 1.5 },
    { name: COLUMNS_NAMES.TITLE, title: t('editableFields.title'), flex: 2 },
    {
      name: COLUMNS_NAMES.ACTIVE, title: t('editableFields.active'),
      flex: 0.5,
      cellRenderer: booleanCellRenderer,
    },
    { name: COLUMNS_NAMES.TEXT, title: t('editableFields.text'), flex: 3, cellRenderer: WysiwygFormatter, wrapText: true },
  ], [t]);
  const newsFeedData = useMemo(() => newsFeed.map((result) => ({
    ...result,
    title: result.title[lang],
    text: result.text[lang],
    creationDate: getFullFormattedDate(result.creationDate, timeZone, SHORT_FULL_DATE_FORMAT),
    publicationDate: getFullFormattedDate(result.publicationDate, timeZone, SHORT_FULL_DATE_FORMAT),
    expirationDate: getFullFormattedDate(result.expirationDate, timeZone, SHORT_FULL_DATE_FORMAT),
  })), [timeZone, lang, newsFeed]);

  const handleDeleteSelected = () => {
    setPopupOpen('');
    administrationServices.deleteNewsfeed(selection)
      .then(getNewsFeed)
      .catch((error) => addToast(error.message, 'ERROR'));
  };

  const currentSelection = useMemo(() => newsFeed.filter((row) => selection.includes(row.id)), [selection, newsFeed]);

  useEffect(() => {
    getNewsFeed();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <AccordionPanel block title={`${t('menu.newsfeed')} ${newsFeed ? `(${newsFeed.length})` : ''}`}>
        <div className={styles.ButtonsTable}>
          <div className={styles.ButtonsContentTableRight}>
            <Button
              id="upload-xls-file"
              buttonStyle="TableButton"
              btnText={t('editableFields.add')}
              handleClick={() => setPopupOpen('CREATION')}
            />
            <IcoButtonSimple
              disabled={selection.length !== 1}
              id="btn-edit-campaign"
              icon="icon-stift-bearbeiten"
              tooltipText={t('campaign.editTitle')}
              handleClick={() => setPopupOpen('EDITION')}
            />
            <IcoButtonSimple
              disabled={!(selection.length > 0)}
              id="btn-delete-plan"
              icon="icon-muelleimer-loeschen"
              tooltipText={t('dialog.delete')}
              handleClick={() => setPopupOpen('DELETION')}
            />
          </div>
        </div>
        <TableListEditableNew
          hasCheckbox
          columnId="id"
          list={newsFeedData}
          columns={columns}
          selection={selection}
          onSelectionChange={(newSelection) => setSelection(newSelection as string[])}
          loading={loading}
        />
      </AccordionPanel>

      {popupOpen === 'DELETION' && (
        <ConfirmDialog
          reject={() => setPopupOpen('')}
          confirm={handleDeleteSelected}
          title={t('dialog.deleteNewsFeeds.title')}
          closeBtnText={t('dialog.cancel')}
          confirmBtnText={t('dialog.remove')}
        >
          <p className={styles.RemovePlantsMessage}>
            {`${t(`dialog.deleteNewsFeeds.${currentSelection.length === 1 ? 'messageSingular' : 'message'}`)}: `}
            <b>{currentSelection.map((nf) => `${nf.title[lang]} (${nf.category})`).join(', ')}</b>
          </p>
        </ConfirmDialog>
      )}
      {['CREATION', 'EDITION'].includes(popupOpen) && (
        <DialogNewsfeed
          title={t('dialog.newsfeed')}
          close={() => setPopupOpen('')}
          editNewsfeed={popupOpen === 'EDITION' ? currentSelection[0] : undefined}
          reloadNewsFeed={getNewsFeed}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  timeZone: getSettingsInfo(state)?.timezone,
});

export default connect(mapStateToProps)(Newsfeed);
